import React, { useEffect, useState, useMemo, useRef } from 'react'
import {
  Box,
  TextField,
  Checkbox,
  MenuItem,
  ListItemText,
  Popover,
  Radio,
  InputAdornment,
  Skeleton,
  CircularProgress,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { MUIButton } from '../../Shared'
import styles from './ListingFilter.module.scss'
import MUIDateTimePicker from '../../MUIDateTimePicker'
import DateRangeFilter, { DateRangeValue } from '../DateRangeFilter'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { CrossFilterIcon } from '../../Icons/Edit'
import { SearchIcon, CheckboxIcon, CheckboxCheckedIcon } from '../../Icons'
import { CustomTooltip } from '../../Shared/CustomToolTip/CustomToolTip'
import RadioCheckedIcon from '../../Icons/RadioCheckedIcon'
import RadioIcon from '../../Icons/RadioIcon'
import useApiPaginatedOptions, { PaginatedFetchResult } from '../../../Hooks/useApiPaginatedOptions'
import useDebounce from '../../../Hooks/useDebounce'

export interface FilterOption {
  key: string
  value: string
}

export interface ListingFilterLazyLoad {
  /** Fetches a page of options from the API. */
  fetchPage: (
    page: number,
    pageSize: number,
    searchText: string,
    signal: AbortSignal,
  ) => Promise<PaginatedFetchResult<FilterOption>>
  /** Number of options requested per page. */
  pageSize?: number
  /**
   * Resolved labels for already-selected keys so selected chips stay visible
   * even when the option is not part of the currently loaded page.
   */
  selectedLabels?: Record<string, string>
}

interface ListingFilterProps {
  lable: string
  id: string
  searchable: boolean
  clearFilter?: boolean
  options: { key: string; value: string }[]
  searchFunction?: (filter: any, id: string) => void
  isFilterSingleSelect?: boolean
  filters?: any
  clearFilterTrigger?: number
  type?: 'date' | 'dateRange'
  autoFocusSearch?: boolean
  isLoading?: boolean
  /** Hydrates the dateRange chip so a retained/persisted range re-displays as active. */
  initialDateRange?: DateRangeValue | null
  /** When provided, options are lazily loaded from the API with search + infinite scroll. */
  lazyLoad?: ListingFilterLazyLoad
}

const SEARCH_DEBOUNCE_MS = 400
const DEFAULT_LAZY_PAGE_SIZE = 10
// Distance (px) from the bottom of the list at which the next page is requested.
const SCROLL_LOAD_THRESHOLD = 60

const SKELETON_PLACEHOLDER_KEYS = ['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4']

const EndIcon = ({ open }: { open: boolean }) => {
  return open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
}

const ListingFilter: React.FC<ListingFilterProps> = ({
  searchable,
  lable,
  id,
  clearFilter,
  options,
  searchFunction,
  isFilterSingleSelect,
  clearFilterTrigger,
  filters,
  type,
  autoFocusSearch = true,
  isLoading = false,
  initialDateRange = null,
  lazyLoad,
}) => {
  const isLazy = Boolean(lazyLoad)
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null)
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [submittedOptions, setSubmittedOptions] = useState<string[]>([]) // Track submitted selections
  const [searchTerm, setSearchTerm] = useState('')
  const [clearLocationSearch, setClearLocationSearch] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [dateRangeValue, setDateRangeValue] = useState<DateRangeValue | null>(initialDateRange)
  const [searchParams] = useSearchParams()

  const open = Boolean(anchorEl)
  const location = useLocation()
  const navigate = useNavigate()

  // Debounced search term drives server-side search in lazy mode.
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS)

  const {
    options: lazyOptions,
    isLoading: lazyLoading,
    isFetchingMore: lazyFetchingMore,
    hasMore: lazyHasMore,
    error: lazyError,
    loadMore: lazyLoadMore,
    retry: lazyRetry,
  } = useApiPaginatedOptions<FilterOption>({
    fetchPage: lazyLoad?.fetchPage ?? (async () => ({ data: [] as FilterOption[], total: 0 })),
    getOptionId: (option) => option.key,
    pageSize: lazyLoad?.pageSize ?? DEFAULT_LAZY_PAGE_SIZE,
    searchText: isLazy ? debouncedSearchTerm : '',
    // Only fetch when the dropdown is open to keep it lazy.
    enabled: isLazy && open,
    logContext: `listing-filter-${id}`,
  })

  const listContainerRef = useRef<HTMLDivElement>(null)

  // Accumulates key -> label for every option seen across pages/searches so a
  // selected option's name stays visible even after reopen/search/pagination.
  const labelCacheRef = useRef<Record<string, string>>({})
  useEffect(() => {
    if (!isLazy) return
    lazyOptions.forEach((opt) => {
      labelCacheRef.current[opt.key] = opt.value
    })
  }, [isLazy, lazyOptions])

  // Merge selected keys (with resolved labels) so chosen options always render.
  const selectedOptionList = useMemo<FilterOption[]>(() => {
    if (!isLazy) return []
    const providedLabels = lazyLoad?.selectedLabels ?? {}
    const fromLoaded = new Map(lazyOptions.map((opt) => [opt.key, opt.value]))
    return submittedOptions.map((key) => ({
      key,
      value: fromLoaded.get(key) ?? labelCacheRef.current[key] ?? providedLabels[key] ?? key,
    }))
  }, [isLazy, lazyOptions, submittedOptions, lazyLoad?.selectedLabels])

  const displayOptions = useMemo(() => {
    if (isLazy) {
      // Server already filtered by searchText; pin submitted (selected) options
      // to the top and drop duplicates that also appear in the loaded page.
      const selectedKeys = new Set(submittedOptions)
      const remaining = lazyOptions.filter((option) => !selectedKeys.has(option.key))
      const pinned = selectedOptionList.filter((option) =>
        option.value?.toLowerCase().includes((debouncedSearchTerm || '').toLowerCase()),
      )
      return [...pinned, ...remaining]
    }
    if (!options || options.length === 0) return []
    const filteredOptions = options.filter((option) =>
      option?.value?.toLowerCase().includes(searchTerm?.toLowerCase()),
    )
    const submittedItems = filteredOptions.filter((option) => submittedOptions.includes(option.key))
    const remainingItems = filteredOptions.filter(
      (option) => !submittedOptions.includes(option.key),
    )

    return [
      ...submittedItems,
      ...remainingItems.filter((option) => !submittedOptions.includes(option.key)),
    ]
  }, [
    isLazy,
    lazyOptions,
    selectedOptionList,
    debouncedSearchTerm,
    options,
    selectedOptions,
    submittedOptions,
    searchTerm,
  ])

  // Keep the latest loader state/guards in a ref so the scroll handler always
  // reads fresh values without being recreated on every render.
  const infiniteScrollRef = useRef({
    hasMore: lazyHasMore,
    isLoading: lazyLoading,
    isFetchingMore: lazyFetchingMore,
    loadMore: lazyLoadMore,
  })
  useEffect(() => {
    infiniteScrollRef.current = {
      hasMore: lazyHasMore,
      isLoading: lazyLoading,
      isFetchingMore: lazyFetchingMore,
      loadMore: lazyLoadMore,
    }
  }, [lazyHasMore, lazyLoading, lazyFetchingMore, lazyLoadMore])

  // Infinite scroll via the list container's own scrollbar: when the user gets
  // within SCROLL_LOAD_THRESHOLD px of the bottom, request the next page. The
  // hook guards against overlapping fetches, so this can be called liberally.
  const maybeLoadMore = () => {
    const container = listContainerRef.current
    if (!container) return
    const { hasMore, isLoading, isFetchingMore, loadMore } = infiniteScrollRef.current
    if (!hasMore || isLoading || isFetchingMore) return
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    if (distanceFromBottom <= SCROLL_LOAD_THRESHOLD) {
      loadMore()
    }
  }

  const handleListScroll = () => {
    if (!isLazy) return
    maybeLoadMore()
  }

  // After each page renders, if the loaded options still don't fill the
  // scrollable area (so there's nothing to scroll yet), keep fetching until the
  // container overflows or there are no more pages.
  useEffect(() => {
    if (!isLazy || !open) return
    const container = listContainerRef.current
    if (!container) return
    if (lazyLoading || lazyFetchingMore || !lazyHasMore) return
    if (container.scrollHeight <= container.clientHeight) {
      lazyLoadMore()
    }
  }, [isLazy, open, lazyLoading, lazyFetchingMore, lazyHasMore, displayOptions.length])

  useEffect(() => {
    const stateParam = new URLSearchParams(location?.search)?.get('state')
    if (stateParam && options?.some((option) => option?.key === stateParam)) {
      setSelectedOptions([stateParam])
      setSubmittedOptions([stateParam]) // Mark as submitted since it comes from URL
    }
  }, [location?.search, options])

  const hasRunClearFilterEffect = useRef(false)
  useEffect(() => {
    if (!hasRunClearFilterEffect.current) {
      hasRunClearFilterEffect.current = true
      return
    }
    if (clearFilter) {
      handleReset()
    }
  }, [clearFilter])

  useEffect(() => {
    if (clearLocationSearch) {
      if (id === 'state') {
        const searchParams = new URLSearchParams(location?.search)
        searchParams?.delete('state')
        navigate(
          {
            pathname: location?.pathname,
            search: searchParams?.toString(),
          },
          { replace: true },
        )
      }
      setClearLocationSearch(false)
    }
  }, [clearLocationSearch])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    const stateParam = searchParams.get('state')
    if (id === 'state' && (!filters || filters.length === 0) && stateParam) {
      setSelectedOptions([stateParam])
    } else {
      setSelectedOptions(submittedOptions)
    }
    setSearchTerm('')
  }

  const handleSelectOption = (key: any) => {
    let updatedSelection = [...selectedOptions]
    if (updatedSelection.includes(key)) {
      updatedSelection = updatedSelection?.filter((option) => option !== key)
    } else {
      updatedSelection = [...updatedSelection, key]
    }
    setSelectedOptions(updatedSelection)
  }

  const handleSingleSelect = (key: any) => {
    setSelectedOptions([key])
  }

  const handleReset = () => {
    if (type === 'date') {
      setSelectedDate(null)
      if (searchFunction) searchFunction({}, id)
    }
    if (type === 'dateRange') {
      setDateRangeValue(null)
      if (searchFunction) searchFunction({}, id)
    }
    if (selectedOptions.length > 0 || (filters && filters.length > 0)) {
      if (searchFunction) searchFunction({}, id)
    } else {
      if (filters?.length > 0 && searchFunction) {
        searchFunction({}, id)
      }
      setSelectedOptions([])
      setSearchTerm('')
      setClearLocationSearch(true)
    }
    setSelectedOptions([])
    setSubmittedOptions([])
    setSearchTerm('')
    setClearLocationSearch(true)
  }

  useEffect(() => {
    if (clearFilterTrigger) {
      setSelectedOptions([])
      setSubmittedOptions([])
      setSearchTerm('')
      setSelectedDate(null)
      setDateRangeValue(null)
    }
  }, [clearFilterTrigger])

  const handleSave = () => {
    const filter: any = {}
    if (type === 'date' && selectedDate) {
      filter.filterBy = id
      filter.filterCondition = 'equals'
      filter.filterValue = selectedDate
    } else if (selectedOptions.length > 0) {
      filter.filterBy = id
      filter.filterCondition = 'in'
      filter.filterValue = selectedOptions
    }
    setSubmittedOptions([...selectedOptions])

    if (searchFunction) searchFunction(filter, id)
    setSearchTerm('')
    setAnchorEl(null)
    setClearLocationSearch(true)
  }

  useEffect(() => {
    if (filters?.length > 0) {
      setSelectedOptions(filters)
      setSubmittedOptions(filters)
    }
  }, [filters])

  useEffect(() => {
    if (type === 'dateRange' && (initialDateRange?.fromDate || initialDateRange?.toDate)) {
      setDateRangeValue(initialDateRange)
    }
  }, [initialDateRange, type])

  const displayFilterBasedOnType = () => {
    if (type === 'date') {
      return (
        <MUIDateTimePicker
          label={lable}
          type='date'
          value={selectedDate}
          onChange={setSelectedDate}
          name={id}
          error={false}
          disableFuture={true}
        />
      )
    }

    if (type === 'dateRange') {
      return (
        <DateRangeFilter
          value={dateRangeValue}
          onApply={(val) => {
            setDateRangeValue(val)
            if (searchFunction) {
              if (val) {
                searchFunction(
                  {
                    filterBy: id,
                    filterCondition: 'range',
                    filterValue: { fromDate: val.fromDate, toDate: val.toDate },
                    quickRange: val.quickRange,
                  },
                  id,
                )
              } else {
                searchFunction({}, id)
              }
            }
            setAnchorEl(null)
          }}
          onReset={() => {
            setDateRangeValue(null)
            if (searchFunction) searchFunction({}, id)
            setAnchorEl(null)
          }}
        />
      )
    }

    if (searchable) {
      return (
        <TextField
          autoFocus={autoFocusSearch}
          placeholder={`Search ${lable}`}
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          className='listing-filter-search'
        />
      )
    }

    return (
      <Box
        sx={{
          alignContent: 'center',
          fontWeight: '600',
          fontSize: '20px',
          paddingLeft: '24px',
          paddingTop: '12px',
        }}
      >
        {lable}
      </Box>
    )
  }
  const renderEndIcon = () => {
    if (selectedOptions?.length > 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div className={`${styles['selected-count']}`}>{selectedOptions?.length}</div>
          <button
            onClick={(e: any) => {
              e.preventDefault()
              e.stopPropagation()
              handleReset()
            }}
            className={`${styles['cross-filter-icon']}`}
          >
            <CrossFilterIcon />
          </button>
        </Box>
      )
    }
    if (type === 'dateRange' && dateRangeValue) {
      return (
        <button
          onClick={(e: any) => {
            e.preventDefault()
            e.stopPropagation()
            setDateRangeValue(null)
            if (searchFunction) searchFunction({}, id)
          }}
          className={`${styles['cross-filter-icon']}`}
        >
          <CrossFilterIcon />
        </button>
      )
    }
    if (type === 'dateRange') {
      return <CalendarMonthIcon sx={{ fontSize: 18 }} />
    }
    return <EndIcon open={open} />
  }

  const renderSkeletons = () =>
    SKELETON_PLACEHOLDER_KEYS.map((skeletonKey) => (
      <MenuItem key={skeletonKey} disabled className={`${styles['filter-option-item']}`}>
        <Skeleton
          variant='rectangular'
          width={16}
          height={16}
          sx={{ mr: 1, borderRadius: '2px', flexShrink: 0 }}
        />
        <Skeleton variant='text' width='60%' />
      </MenuItem>
    ))

  const renderOptionItem = (item: FilterOption) => (
    <MenuItem
      key={item.key}
      value={item.key}
      onClick={() => {
        isFilterSingleSelect ? handleSingleSelect(item.key) : handleSelectOption(item.key)
      }}
      className={`${styles['filter-option-item']}`}
    >
      {isFilterSingleSelect ? (
        <Radio
          data-testid={`filter_checkbox_${item.key}`}
          checked={selectedOptions[0] === item.key}
          className={styles['filter-checkbox']}
          checkedIcon={<RadioCheckedIcon />}
          icon={<RadioIcon />}
        />
      ) : (
        <Checkbox
          icon={<CheckboxIcon />}
          checkedIcon={<CheckboxCheckedIcon />}
          className={styles['filter-checkbox']}
          data-testid={`filter_checkbox_${item.key}`}
          checked={selectedOptions.includes(item.key)}
        />
      )}
      <CustomTooltip
        title={item.value}
        placement='left'
        arrow
        disableInteractive
        slotProps={{
          tooltip: {
            sx: {
              position: 'relative',
              right: '2.6rem',
              mr: '0.5rem',
              color: 'white',
              backgroundColor: '#402080',
              height: '32px',
              fontSize: '14px',
              '& .MuiTooltip-arrow': {
                color: '#402080',
              },
            },
          },
        }}
      >
        <ListItemText primary={item.value} className={`${styles['filter-text']}`} />
      </CustomTooltip>
    </MenuItem>
  )

  const renderLazyState = () => {
    // Error state (no options loaded yet) with retry.
    if (lazyError && displayOptions.length === 0) {
      return (
        <div data-testid={`filter_error_${id}`} className={styles['filter-status-message']}>
          <span>Something went wrong.</span>
          <button
            type='button'
            data-testid={`filter_retry_${id}`}
            className={styles['filter-retry-btn']}
            onClick={() => lazyRetry()}
          >
            Retry
          </button>
        </div>
      )
    }
    // Empty state.
    if (!lazyLoading && displayOptions.length === 0) {
      return (
        <div data-testid={`filter_empty_${id}`} className={styles['filter-status-message']}>
          No options found
        </div>
      )
    }
    return (
      <>
        {displayOptions.map(renderOptionItem)}
        {lazyFetchingMore && (
          <div className={styles['filter-infinite-sentinel']}>
            <CircularProgress size={18} />
          </div>
        )}
        {lazyError && displayOptions.length > 0 && (
          <div className={styles['filter-status-message']}>
            <button
              type='button'
              data-testid={`filter_retry_${id}`}
              className={styles['filter-retry-btn']}
              onClick={() => lazyRetry()}
            >
              Retry
            </button>
          </div>
        )}
      </>
    )
  }

  const renderListBody = () => {
    if (isLazy) {
      if (lazyLoading && displayOptions.length === 0) return renderSkeletons()
      return renderLazyState()
    }
    if (isLoading) return renderSkeletons()
    return displayOptions.map(renderOptionItem)
  }

  return (
    <>
      <MUIButton
        className={`listing-filter-btn ${selectedOptions?.length && 'active'}`}
        type='button'
        label={lable}
        endIcon={renderEndIcon()}
        handleClickCB={handleClick}
        sx={{
          borderRadius: '8px',
          opacity:
            (type === 'date' && selectedDate) ||
            (type === 'dateRange' && dateRangeValue) ||
            selectedOptions?.length > 0
              ? 1
              : 0.7,
          backgroundColor: '#ffffff',
          border:
            (type === 'date' && selectedDate) ||
            (type === 'dateRange' && dateRangeValue) ||
            selectedOptions?.length > 0
              ? '2px solid #402080'
              : '',
          minWidth: 'max-content',
          '&:hover': {
            border:
              (type === 'date' && selectedDate) ||
              (type === 'dateRange' && dateRangeValue) ||
              selectedOptions?.length > 0
                ? '2px solid #402080'
                : '',
          },
        }}
        nonDisableProp
      />
      <Popover
        data-testid='popover_dialog'
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          marginTop: '15px',
          borderRadius: '15px',
          '& .MuiPopover-paper': { width: type === 'dateRange' ? 'max-content' : undefined },
        }}
        className='filter-option-container'
      >
        {type === 'dateRange' ? (
          displayFilterBasedOnType()
        ) : (
          <div className={styles['filter-option']}>
            <h5>{lable}</h5>
            {displayFilterBasedOnType()}

            <div
              ref={listContainerRef}
              className={`${styles['filter-options-list']}`}
              onScroll={handleListScroll}
            >
              {renderListBody()}
            </div>
            <div className={styles['filter-popover-footer']}>
              <MUIButton
                width={50}
                fontSize={14}
                height='36px'
                type='button'
                label={'Apply'}
                variant='contained'
                handleClickCB={handleSave}
                nonDisableProp
              />
              <MUIButton
                width={50}
                fontSize={14}
                className={styles['reset-filters-btn']}
                height='36px'
                type='button'
                label={'Reset'}
                variant='text'
                handleClickCB={handleReset}
              />
            </div>
          </div>
        )}
      </Popover>
    </>
  )
}

export default ListingFilter
